import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { ArrowLeft, ScanLine, Upload, MapPin, Flag, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { makeApi } from "@/lib/api";
import { ApptLine } from "@/components/loads/ApptLine";
import DocViewer from "@/components/DocViewer";
import { DocItem } from "@/components/DocItem";
import { DeleteDocDialog } from "@/components/DeleteDocDialog";
import { DocScanner, PhotoInputs } from "@/components/scanner/DocScanner";
import { LumperPanel, PaidByBadge } from "@/components/lumper/LumperPanel";
import { useDriverMe } from "@/components/auth/DriverGate";

export default function DriverLoadDetail() {
  const { id } = useParams();
  const { getToken } = useAuth();
  const userId = useDriverMe()?.user_id;
  const [load, setLoad] = useState(null);
  const [missing, setMissing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [scanning, setScanning] = useState(null);
  const scanCamRef = useRef(null);
  const scanGalRef = useRef(null);
  const pendingScan = useRef(null);
  // Opens the phone's own camera straight from the tap; the photo then opens the scanner.
  const startScan = (target) => { pendingScan.current = target; scanCamRef.current?.click(); };
  const [lumperOpen, setLumperOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await makeApi(getToken).get(`/loads/${id}`);
      setLoad(data);
    } catch (e) {
      if (e?.response?.status === 404) setMissing(true);
      else toast.error("Could not open this load. Check your signal and try again.");
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (missing) {
    return (
      <div className="space-y-4 text-center py-12" data-testid="driver-load-missing">
        <p className="font-medium">This load is no longer assigned to you.</p>
        <Link to="/driver" className="text-[#002FA7] font-medium">Back to my loads</Link>
      </div>
    );
  }
  if (!load) return <div className="text-slate-500">Loading…</div>;

  const upload = async (files, type = "POD", meta = null) => {
    if (!files || files.length === 0) return false;
    setUploading(true);
    try {
      const api = makeApi(getToken);
      for (const f of files) {
        const fd = new FormData();
        fd.append("type", type);
        fd.append("file", f);
        if (meta) { fd.append("paid_by", meta.paid_by); fd.append("amount", meta.amount || ""); }
        await api.post(`/loads/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(type === "Lumper" ? "Lumper receipt uploaded" : files.length > 1 ? `${files.length} files uploaded` : "POD uploaded");
      if (type === "Lumper") setLumperOpen(false);
      refresh();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      return false;
    } finally {
      setUploading(false);
    }
  };

  const saveScan = async (file) => {
    if (!(await upload([file], scanning?.type, scanning?.meta))) throw new Error("upload failed");
  };

  const docs = (load.documents || []).filter((d) => d.type === "POD" || d.type === "Lumper" || d.uploaded_by === userId);
  const openDoc = (d) => setViewer({ kind: "document", id: d.id, filename: d.original_filename, content_type: d.content_type });

  return (
    <div className="space-y-5" data-testid="driver-load-detail">
      <Link to={load.finished ? "/driver?tab=previous" : "/driver"} className="inline-flex items-center gap-1.5 text-sm text-slate-500" data-testid="driver-back-link">
        <ArrowLeft className="w-4 h-4" /> All my loads
      </Link>

      <Card className="p-5 border-slate-200">
        <div className="text-xs text-slate-500" data-testid="driver-load-numbers">Load #{load.carrier_number || load.load_number}{load.carrier_number && ` · Broker ref #${load.load_number}`}</div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{load.broker_name}</h1>
        <div className="mt-3 space-y-2 text-sm text-slate-700" data-testid="driver-load-addresses">
          <div className="flex gap-2">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#002FA7]" />
            <div><div className="text-xs text-slate-500">Pickup</div>{load.pickup_location}<div><ApptLine date={load.pickup_date} appt={load.pickup_appt} testId="driver-pickup-appt" className="mt-1" /></div></div>
          </div>
          <div className="flex gap-2">
            <Flag className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
            <div><div className="text-xs text-slate-500">Delivery</div>{load.delivery_location}<div><ApptLine date={load.delivery_date} appt={load.delivery_appt} testId="driver-delivery-appt" className="mt-1" /></div></div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-3">
        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid="file-input" />
        <Button
          onClick={() => startScan({ type: "POD" })}
          disabled={uploading}
          className="h-14 text-base bg-[#002FA7] hover:bg-[#001A75] rounded-xl"
          data-testid="scan-pod-btn"
        >
          <ScanLine className="w-5 h-5 mr-2" />
          {uploading ? "Uploading…" : "Scan POD"}
        </Button>
        <p className="-mt-1 text-center text-xs text-slate-500">Scan one or more pages. They are saved as one clean PDF.</p>
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          variant="outline"
          className="h-12 rounded-xl"
          data-testid="upload-pod-btn"
        >
          <Upload className="w-5 h-5 mr-2" /> Upload from gallery
        </Button>
        {lumperOpen ? (
          <LumperPanel
            busy={uploading}
            onScan={(meta) => startScan({ type: "Lumper", meta })}
            onFile={(f, meta) => upload([f], "Lumper", meta)}
            onCancel={() => setLumperOpen(false)}
          />
        ) : (
          <Button onClick={() => setLumperOpen(true)} disabled={uploading} variant="outline" className="h-12 rounded-xl" data-testid="add-lumper-btn">
            <Receipt className="w-5 h-5 mr-2" /> Add lumper receipt
          </Button>
        )}
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Paperwork uploaded ({docs.length})</div>
        {docs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            No POD yet. Scan or upload one after delivery.
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <DocItem key={d.id} doc={d} onOpen={openDoc} showUploader={false} onDelete={d.uploaded_by === userId ? setDeleting : undefined}
                extra={d.type === "Lumper" ? <><span className="pill bg-blue-50 text-[#002FA7] border border-blue-200">Lumper</span><PaidByBadge doc={d} /></> : null} />
            ))}
          </div>
        )}
      </div>

      <DocViewer open={!!viewer} onClose={() => setViewer(null)} item={viewer} />
      <DeleteDocDialog doc={deleting} onClose={() => setDeleting(null)} onDeleted={refresh} trashNote="It goes to your dispatcher's Trash, and they can restore it within 30 days." />
      <PhotoInputs cameraRef={scanCamRef} galleryRef={scanGalRef} onFiles={(files) => setScanning({ ...pendingScan.current, files })} />
      <div className="!mt-0"><DocScanner open={!!scanning} initialFiles={scanning?.files} loadNumber={load.load_number} prefix={scanning?.type === "Lumper" ? "Lumper" : "POD"} onClose={() => setScanning(null)} onSave={saveScan} /></div>
    </div>
  );
}
